import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/ui';
import { parseServerDate } from '../i18n/format';

interface LicenseStatus {
  state: 'valid' | 'grace' | 'expired' | 'trial' | 'unlicensed';
  mode: 'off' | 'warn' | 'enforce';
  customer?: string; tier?: string; features: string[]; maxAgents: number | null; agentsInUse: number;
  licenseId?: string; issuedAt?: string; expiresAt?: string; daysLeft?: number; message: string;
}

const ALL_FEATURES: Array<[string, string, string]> = [
  ['endpoint-agents', 'Endpoint agents', 'Endpoint agent, DLP proxy and shadow-AI discovery on laptops'],
  ['shadow-ai', 'Shadow AI', 'Discovery, provider catalogue and AI app policy'],
  ['dlp', 'Data loss prevention', 'DLP rules and activity'],
  ['gateway', 'AI gateway', 'Model switching, token budgets and cost'],
  ['mcp', 'MCP gateway', 'MCP server gateway and broker'],
  ['a2a', 'Agent-to-agent', 'Delegation, topic domains and provenance'],
  ['jit', 'Just-in-time access', 'JIT requests and approvals'],
  ['reporting', 'Reporting', 'Reports and scheduled exports'],
];

const License: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pages.license.${k}`, { defaultValue: d });
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/admin/license`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
  }, [apiUrl]);
  useEffect(() => { load(); }, [load]);

  const install = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const body = JSON.parse(await file.text());
      const r = await fetch(`${apiUrl}/admin/license`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setStatus(j); setMsg({ ok: true, text: tt('installed', 'License installed.') });
    } catch (e: any) {
      if (e?.name !== 'AbortError') setMsg({ ok: false, text: e instanceof SyntaxError ? tt('notJson', 'That file is not a license file.') : e.message });
    } finally { setBusy(false); }
  };

  const badge: Record<string, string> = { valid: 'Valid', grace: 'Grace period', expired: 'Expired', trial: 'Trial', unlicensed: 'Unlicensed' };
  const licensed = (f: string) => !!status?.features.includes(f);

  return (
    <div className="page">
      <h1 className="page-title">{tt('title', 'License')}</h1>
      <p style={{ color: 'var(--text-2)' }}>{tt('subtitle', 'What this installation is licensed for. Licenses are signed files issued by Agenti5 and work offline.')}</p>

      {status && (
        <div className="ent-card" style={{ marginBottom: 16 }}>
          <div className="ent-card-head">
            <span><Icon name="key" /> {status.customer || tt('noLicense', 'No license installed')}</span>
            <span className="ent-badge">{badge[status.state]}</span>
          </div>
          <div style={{ padding: 14, display: 'grid', gap: 6, fontSize: 14 }}>
            <div>{status.message}</div>
            {status.tier && <div><strong>{tt('tier', 'Tier')}:</strong> {status.tier}</div>}
            {status.expiresAt && (
              <div><strong>{tt('expires', 'Expires')}:</strong> {parseServerDate(status.expiresAt).toLocaleDateString()}
                {typeof status.daysLeft === 'number' && status.state !== 'trial' && ` (${status.daysLeft >= 0 ? `${status.daysLeft} ${tt('daysLeft', 'days left')}` : tt('past', 'past')})`}
              </div>
            )}
            <div>
              <strong>{tt('agents', 'Endpoint agents')}:</strong> {status.agentsInUse} / {status.maxAgents ?? tt('unlimited', 'unlimited')}
              {status.maxAgents != null && status.agentsInUse > status.maxAgents && <span style={{ color: 'var(--danger, #d64545)' }}> {tt('overLimit', 'over the licensed count')}</span>}
            </div>
            <div style={{ color: 'var(--text-2)', fontSize: 12 }}>
              {status.mode === 'enforce' ? tt('modeEnforce', 'Enforcement is on: unlicensed features are blocked.')
                : status.mode === 'warn' ? tt('modeWarn', 'Warn-only mode: nothing is blocked yet. Features marked "not licensed" would be blocked when enforcement is switched on (AGENTI5_LICENSE_ENFORCE=enforce).')
                : tt('modeOff', 'Licensing is not enforced.')}
            </div>
          </div>
        </div>
      )}

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head"><span>{tt('features', 'Features')}</span></div>
        <div className="ent-table-wrap">
          <table className="ent-table">
            <tbody>
              {ALL_FEATURES.map(([key, name, desc]) => (
                <tr key={key}>
                  <td style={{ width: 180 }}><strong>{name}</strong></td>
                  <td style={{ color: 'var(--text-2)' }}>{desc}</td>
                  <td style={{ width: 120 }}>{licensed(key) ? <span className="ent-badge">{tt('licensed', 'Licensed')}</span> : <span style={{ color: 'var(--text-2)' }}>{tt('notLicensed', 'Not licensed')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head"><span>{tt('install', 'Install or renew a license')}</span></div>
        <div style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".json,application/json" disabled={busy} onChange={(e) => install(e.target.files?.[0])} />
          {status?.licenseId && (
            <button className="ent-btn danger" disabled={busy}
              onClick={async () => { setBusy(true); try { await fetch(`${apiUrl}/admin/license`, { method: 'DELETE' }); await load(); } catch { /* cancelled */ } finally { setBusy(false); } }}>
              {tt('remove', 'Remove license')}
            </button>
          )}
        </div>
        {msg && <div style={{ padding: '0 14px 14px', color: msg.ok ? 'var(--ok, #2e9e5b)' : 'var(--danger, #d64545)' }}>{msg.text}</div>}
      </div>
    </div>
  );
};

export default License;
